import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Send,
  Eye,
  EyeOff,
  MousePointer,
  AlertCircle,
  Clock,
  CheckCircle2,
  Edit3,
  Loader2,
  Trash2,
  FileText,
  BarChart2,
  Activity,
  RefreshCw,
  X,
  Copy,
  CheckSquare,
  Square,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";
import {
  fetchNewsletters,
  fetchNewsletterDrafts,
  fetchNewsletterDraft,
  saveNewsletterDraft,
  deleteNewsletterDraft,
  hideInsightsNewsletter,
  resendToUnopened,
  hideGhlCampaign,
  fmtDate,
} from "../services/api";
import { Badge, Button, Table } from "../components/ui";
import { useTenant } from "../contexts/TenantContext";
import { getGraphToken } from "../services/msal";
import { useAuth } from "../hooks/useAuth";

export default function Newsletters() {
  const navigate = useNavigate();
  const { features } = useTenant();
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const hasGhl = features?.ghl !== false;
  const [campaigns, setCampaigns] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [stats, setStats] = useState({});
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resendTarget, setResendTarget] = useState(null); // newsletter to resend
  const [resendPrefix, setResendPrefix] = useState("ICYMI: ");
  const [resendMethod, setResendMethod] = useState("sendgrid");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [selectedInsights, setSelectedInsights] = useState(new Set());
  const [selectedGhl, setSelectedGhl] = useState(new Set());
  const [bulkHiding, setBulkHiding] = useState(false);

  const handleResend = async () => {
    if (!resendTarget) return;
    setResendLoading(true);
    setResendResult(null);
    try {
      let graphToken = null;
      if (resendMethod === "o365" || resendMethod === "draft") {
        graphToken = await getGraphToken();
      }
      const result = await resendToUnopened(resendTarget.id, {
        sendMethod: resendMethod,
        subjectPrefix: resendPrefix,
        graphToken,
      });
      setResendResult(result);
    } catch (err) {
      setResendResult({ error: err.message });
    } finally {
      setResendLoading(false);
    }
  };

  const loadDrafts = () =>
    fetchNewsletterDrafts({ includeHidden: showHidden })
      .then((data) => setDrafts(data.newsletters || []))
      .catch(() => {});

  const loadCampaigns = () =>
    fetchNewsletters({ includeHidden: showHidden }).then((data) => {
      setCampaigns(data.campaigns || []);
      setStats(data.stats || {});
      setStatusCounts(data.statusCounts || {});
    });

  useEffect(() => {
    const promises = [loadDrafts()];
    if (hasGhl) {
      promises.push(loadCampaigns());
    }
    Promise.all(promises)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [showHidden]);

  const handleBulkHideInsights = async () => {
    if (!selectedInsights.size) return;
    if (!window.confirm(`Hide ${selectedInsights.size} newsletter${selectedInsights.size > 1 ? "s" : ""}?`)) return;
    setBulkHiding(true);
    try {
      await Promise.all([...selectedInsights].map((id) => hideInsightsNewsletter(id)));
      setSelectedInsights(new Set());
      loadDrafts();
    } catch (err) {
      alert(`Bulk hide failed: ${err.message}`);
    } finally {
      setBulkHiding(false);
    }
  };

  const handleBulkHideGhl = async () => {
    if (!selectedGhl.size) return;
    if (!window.confirm(`Hide ${selectedGhl.size} campaign${selectedGhl.size > 1 ? "s" : ""}?`)) return;
    setBulkHiding(true);
    try {
      await Promise.all([...selectedGhl].map((id) => hideGhlCampaign(id)));
      setSelectedGhl(new Set());
      loadCampaigns();
    } catch (err) {
      alert(`Bulk hide failed: ${err.message}`);
    } finally {
      setBulkHiding(false);
    }
  };

  const toggleInsight = (id) => setSelectedInsights((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleGhl = (id) => setSelectedGhl((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAllInsights = () => {
    const sentDrafts = drafts.filter((d) => d.status === "sent" && !d.hidden);
    setSelectedInsights((prev) =>
      prev.size === sentDrafts.length ? new Set() : new Set(sentDrafts.map((d) => d.id))
    );
  };

  const toggleAllGhl = () => {
    const visible = campaigns.filter((c) => !c._hidden);
    setSelectedGhl((prev) =>
      prev.size === visible.length ? new Set() : new Set(visible.map((c) => c.id))
    );
  };

  const statusIcons = {
    completed: CheckCircle2,
    scheduled: Clock,
    draft: Edit3,
  };
  const statusColors = {
    completed: colors.success,
    scheduled: colors.warning,
    draft: colors.textSecondary,
  };

  const campaignColumns = [
    ...(isAdmin
      ? [
          {
            key: "_select",
            label: (() => {
              const visible = campaigns.filter((c) => !c._hidden);
              const allSelected = visible.length > 0 && selectedGhl.size === visible.length;
              return (
                <span onClick={(e) => { e.stopPropagation(); toggleAllGhl(); }} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                  {allSelected ? <CheckSquare size={14} color={colors.accent} /> : <Square size={14} color={colors.textSecondary} />}
                </span>
              );
            })(),
            render: (row) =>
              !row._hidden ? (
                <span onClick={(e) => { e.stopPropagation(); toggleGhl(row.id); }} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                  {selectedGhl.has(row.id) ? <CheckSquare size={14} color={colors.accent} /> : <Square size={14} color={colors.textSecondary} />}
                </span>
              ) : null,
          },
        ]
      : []),
    {
      key: "name",
      label: "Newsletter",
      sortValue: (row) => (row.name || "").toLowerCase(),
      render: (row) => (
        <div style={{ opacity: row._hidden ? 0.5 : 1 }}>
          <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
            {row.name}
            {row._hidden && <EyeOff size={12} color={colors.textSecondary} />}
          </div>
          {row.subject && row.subject !== row.name && (
            <div
              style={{
                fontSize: 12,
                color: colors.textSecondary,
                marginTop: 2,
              }}
            >
              {row.subject}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortValue: (row) => (row.status || "").toLowerCase(),
      render: (row) => {
        const Icon = statusIcons[row.status] || Edit3;
        return (
          <Badge
            color={statusColors[row.status] || colors.textSecondary}
            bg={`${statusColors[row.status] || colors.textSecondary}18`}
          >
            <Icon size={10} />
            {row.status === "completed" ? "sent" : row.status}
          </Badge>
        );
      },
    },
    {
      key: "recipients",
      label: "Delivered",
      sortValue: (row) => row.recipients || 0,
      render: (row) => (
        <span style={{ fontFamily: fonts.mono, fontSize: 12 }}>
          {row.recipients > 0 ? row.recipients.toLocaleString() : "—"}
        </span>
      ),
    },
    {
      key: "totalCount",
      label: "Attempted",
      sortValue: (row) => row.totalCount || 0,
      render: (row) => (
        <span
          style={{
            fontFamily: fonts.mono,
            fontSize: 12,
            color: colors.textSecondary,
          }}
        >
          {row.totalCount > 0 ? row.totalCount.toLocaleString() : "—"}
        </span>
      ),
    },
    {
      key: "failed",
      label: "Failed",
      sortValue: (row) => row.failed || 0,
      render: (row) => {
        if (!row.failed)
          return <span style={{ color: colors.textSecondary }}>—</span>;
        return (
          <span
            style={{
              fontFamily: fonts.mono,
              fontSize: 12,
              color: colors.danger,
            }}
          >
            {row.failed.toLocaleString()}
          </span>
        );
      },
    },
    {
      key: "date",
      label: "Date",
      sortValue: (row) => { const d = row.sentAt || row.scheduledAt || row.createdAt; return d ? new Date(d).getTime() : 0; },
      render: (row) => {
        const d = row.sentAt || row.scheduledAt || row.createdAt;
        return (
          <span
            style={{
              fontFamily: fonts.mono,
              fontSize: 12,
              color: colors.textSecondary,
            }}
          >
            {d ? fmtDate(d) : "—"}
          </span>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            label: "",
            render: (row) => (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm("Hide this GHL campaign?")) return;
                  try {
                    await hideGhlCampaign(row.id);
                    loadCampaigns();
                  } catch (err) {
                    alert(`Hide failed: ${err.message}`);
                  }
                }}
                title="Hide campaign"
                style={{
                  background: "none",
                  border: "none",
                  color: colors.danger,
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <EyeOff size={14} />
              </button>
            ),
          },
        ]
      : []),
  ];

  const statCards = [
    {
      icon: Send,
      label: "Total Delivered",
      value: (stats.totalSent || 0).toLocaleString(),
    },
    {
      icon: Eye,
      label: "Total Attempted",
      value: (stats.totalAttempted || 0).toLocaleString(),
    },
    {
      icon: CheckCircle2,
      label: "Success Rate",
      value: `${stats.successRate || 0}%`,
    },
    {
      icon: AlertCircle,
      label: "Total Failed",
      value: (stats.totalFailed || 0).toLocaleString(),
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <Loader2
          size={24}
          color={colors.accent}
          className="spin"
        />      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: colors.danger }}>
        <AlertCircle size={24} style={{ marginBottom: 8 }} />
        <div>{error}</div>
      </div>
    );
  }

  const completed = statusCounts.completed || 0;

  return (
    <div style={{ paddingBottom: 60 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 28,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 4,
            }}
          >
            Newsletters
          </h1>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: 14,
              color: colors.textSecondary,
            }}
          >
            {campaigns.length} newsletters · {completed} completed
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {isAdmin && (
            <Button
              variant={showHidden ? "primary" : "ghost"}
              icon={showHidden ? Eye : EyeOff}
              onClick={() => setShowHidden((v) => !v)}
            >
              {showHidden ? "Showing Hidden" : "Show Hidden"}
            </Button>
          )}
          <Button
            variant="secondary"
            icon={Activity}
            onClick={() => navigate("/email-activity")}
          >
            Email Activity
          </Button>
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate("/editor")}
          >
            New Newsletter
          </Button>
        </div>
      </div>

      {/* Newsletter Stats */}
      <div
        style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}
      >
        {statCards.map((s) => (
          <div
            key={s.label}
            onClick={() => navigate("/engagement")}
            style={{
              flex: 1,
              minWidth: 160,
              padding: "16px 20px",
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              cursor: "pointer",
              transition: "border-color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = colors.accent)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = colors.border)}
          >
            <s.icon
              size={16}
              color={colors.accent}
              style={{ marginBottom: 8 }}
            />
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                fontFamily: fonts.heading,
                color: colors.text,
              }}
            >
              {s.value}
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Insights Drafts & Sent */}
      {drafts.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <h2
              style={{
                fontFamily: fonts.heading,
                fontSize: 18,
                fontWeight: 700,
                color: colors.text,
                display: "flex",
                alignItems: "center",
                gap: 8,
                margin: 0,
              }}
            >
              <FileText size={18} color={colors.accent} />
              Insights Newsletters
            </h2>
          </div>
          <Table
            columns={[
              ...(isAdmin
                ? [
                    {
                      key: "_select",
                      label: (() => {
                        const sentDrafts = drafts.filter((d) => d.status === "sent" && !d.hidden);
                        const allSelected = sentDrafts.length > 0 && selectedInsights.size === sentDrafts.length;
                        return (
                          <span onClick={(e) => { e.stopPropagation(); toggleAllInsights(); }} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                            {allSelected ? <CheckSquare size={14} color={colors.accent} /> : <Square size={14} color={colors.textSecondary} />}
                          </span>
                        );
                      })(),
                      render: (row) =>
                        row.status === "sent" && !row.hidden ? (
                          <span onClick={(e) => { e.stopPropagation(); toggleInsight(row.id); }} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                            {selectedInsights.has(row.id) ? <CheckSquare size={14} color={colors.accent} /> : <Square size={14} color={colors.textSecondary} />}
                          </span>
                        ) : null,
                    },
                  ]
                : []),
              {
                key: "title",
                label: "Newsletter",
                sortValue: (row) => (row.title || row.subject || "").toLowerCase(),
                render: (row) => (
                  <div style={{ opacity: row.hidden ? 0.5 : 1 }}>
                    <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                      {row.title || row.subject}
                      {row.hidden && <EyeOff size={12} color={colors.textSecondary} />}
                    </div>
                    {row.subject && row.subject !== row.title && (
                      <div
                        style={{
                          fontSize: 12,
                          color: colors.textSecondary,
                          marginTop: 2,
                        }}
                      >
                        {row.subject}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: "segment",
                label: "Segment",
                sortValue: (row) => (row.segment || "all").toLowerCase(),
                render: (row) => (
                  <span
                    style={{
                      fontSize: 12,
                      color: colors.textSecondary,
                      textTransform: "capitalize",
                    }}
                  >
                    {row.segment || "all"}
                  </span>
                ),
              },
              {
                key: "status",
                label: "Status",
                sortValue: (row) => (row.status || "").toLowerCase(),
                render: (row) => {
                  const map = {
                    draft: {
                      color: colors.textSecondary,
                      icon: Edit3,
                      label: "draft",
                    },
                    sending: {
                      color: colors.warning,
                      icon: Clock,
                      label: "sending",
                    },
                    sent: {
                      color: colors.success,
                      icon: CheckCircle2,
                      label: "sent",
                    },
                    error: {
                      color: colors.danger,
                      icon: AlertCircle,
                      label: "error",
                    },
                  };
                  const s = map[row.status] || map.draft;
                  return (
                    <Badge color={s.color} bg={`${s.color}18`}>
                      <s.icon size={10} />
                      {s.label}
                    </Badge>
                  );
                },
              },
              {
                key: "sendResults",
                label: "Results",
                sortValue: (row) => row.sendResults?.sent || 0,
                render: (row) => {
                  if (!row.sendResults)
                    return <span style={{ color: colors.textSecondary }}>—</span>;
                  return (
                    <span
                      style={{ fontFamily: fonts.mono, fontSize: 12, cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/newsletters/${row.id}/analytics`);
                      }}
                      title="View analytics"
                    >
                      <span style={{ color: colors.success }}>
                        {row.sendResults.sent}
                      </span>
                      {" / "}
                      <span style={{ color: colors.textSecondary }}>
                        {row.sendResults.total}
                      </span>
                      {row.sendResults.failed > 0 && (
                        <span style={{ color: colors.danger, marginLeft: 4 }}>
                          ({row.sendResults.failed} failed)
                        </span>
                      )}
                    </span>
                  );
                },
              },
              {
                key: "date",
                label: "Date",
                sortValue: (row) => { const d = row.sentAt || row.updatedAt || row.createdAt; return d ? new Date(d).getTime() : 0; },
                render: (row) => {
                  const d = row.sentAt || row.updatedAt || row.createdAt;
                  return (
                    <span
                      style={{
                        fontFamily: fonts.mono,
                        fontSize: 12,
                        color: colors.textSecondary,
                      }}
                    >
                      {d ? fmtDate(d) : "—"}
                    </span>
                  );
                },
              },
              {
                key: "actions",
                label: "",
                render: (row) => (
                  <div style={{ display: "flex", gap: 4 }}>
                    {row.status === "sent" && (
                      <>
                        <button
                          onClick={() =>
                            navigate(`/newsletters/${row.id}/analytics`)
                          }
                          title="View Analytics"
                          style={{
                            background: "none",
                            border: "none",
                            color: colors.info,
                            cursor: "pointer",
                            padding: 4,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: 12,
                          }}
                        >
                          <BarChart2 size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setResendTarget(row);
                            setResendResult(null);
                            setResendLoading(false);
                          }}
                          title="Resend to unopened"
                          style={{
                            background: "none",
                            border: "none",
                            color: colors.accent,
                            cursor: "pointer",
                            padding: 4,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <RefreshCw size={14} />
                        </button>
                      </>
                    )}
                    {(row.status === "sent" || row.status === "error") && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const original = await fetchNewsletterDraft(row.id);
                            const copy = await saveNewsletterDraft({
                              title: `Copy of ${original.title || original.subject || "Newsletter"}`,
                              subject: original.subject || "",
                              preheader: original.preheader || "",
                              bodyHtml: original.bodyHtml || "",
                              mjmlTemplate: original.mjmlTemplate || "",
                              templateId: original.templateId || "blank",
                              segment: original.segment || "all",
                            });
                            navigate(`/editor?draft=${copy.id}`);
                          } catch (err) {
                            alert(`Duplicate failed: ${err.message}`);
                          }
                        }}
                        title="Duplicate as new draft"
                        style={{
                          background: "none",
                          border: "none",
                          color: colors.accent,
                          cursor: "pointer",
                          padding: 4,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Copy size={14} />
                      </button>
                    )}
                    {row.status === "draft" && (
                      <button
                        onClick={() => navigate(`/editor?draft=${row.id}`)}
                        title="Edit"
                        style={{
                          background: "none",
                          border: "none",
                          color: colors.accent,
                          cursor: "pointer",
                          padding: 4,
                        }}
                      >
                        <Edit3 size={14} />
                      </button>
                    )}
                    {(row.status === "draft" || row.status === "error") && (
                      <button
                        onClick={async () => {
                          if (!window.confirm("Delete this newsletter?"))
                            return;
                          try {
                            await deleteNewsletterDraft(row.id);
                            loadDrafts();
                          } catch (err) {
                            alert(`Delete failed: ${err.message}`);
                          }
                        }}
                        title="Delete"
                        style={{
                          background: "none",
                          border: "none",
                          color: colors.danger,
                          cursor: "pointer",
                          padding: 4,
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    {row.status === "sent" && isAdmin && (
                      <button
                        onClick={async () => {
                          if (!window.confirm("Archive this newsletter? It will be hidden from all users except you."))
                            return;
                          try {
                            await hideInsightsNewsletter(row.id);
                            loadDrafts();
                          } catch (err) {
                            alert(`Archive failed: ${err.message}`);
                          }
                        }}
                        title="Archive (hide)"
                        style={{
                          background: "none",
                          border: "none",
                          color: colors.danger,
                          cursor: "pointer",
                          padding: 4,
                        }}
                      >
                        <EyeOff size={14} />
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            data={drafts}
          />
        </div>
      )}

      {/* GHL Campaigns — only shown when GHL is enabled */}
      {hasGhl && campaigns.length > 0 && (
        <div>
          <h2
            style={{
              fontFamily: fonts.heading,
              fontSize: 18,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 12,
            }}
          >
            GHL Campaigns
          </h2>
          <Table columns={campaignColumns} data={campaigns} />
        </div>
      )}

      {/* Bulk hide floating bar */}
      {isAdmin && (selectedInsights.size > 0 || selectedGhl.size > 0) && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: colors.bgCard, border: `2px solid ${colors.accent}`, borderRadius: 12,
          padding: "12px 24px", display: "flex", alignItems: "center", gap: 16,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)", zIndex: 9998,
        }}>
          <span style={{ fontSize: 13, color: colors.text, fontWeight: 600 }}>
            {selectedInsights.size + selectedGhl.size} selected
          </span>
          {selectedInsights.size > 0 && (
            <Button variant="danger" size="sm" icon={bulkHiding ? Loader2 : EyeOff} onClick={handleBulkHideInsights} disabled={bulkHiding}>
              Hide {selectedInsights.size} Insight{selectedInsights.size > 1 ? "s" : ""}
            </Button>
          )}
          {selectedGhl.size > 0 && (
            <Button variant="danger" size="sm" icon={bulkHiding ? Loader2 : EyeOff} onClick={handleBulkHideGhl} disabled={bulkHiding}>
              Hide {selectedGhl.size} Campaign{selectedGhl.size > 1 ? "s" : ""}
            </Button>
          )}
          <button
            onClick={() => { setSelectedInsights(new Set()); setSelectedGhl(new Set()); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: colors.textSecondary, padding: 4, display: "flex", alignItems: "center" }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Resend to Unopened modal */}
      {resendTarget && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.5)", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}
          onClick={() => !resendLoading && setResendTarget(null)}
        >
          <div
            style={{
              background: colors.bgCard, borderRadius: 10,
              width: "min(90vw, 420px)", padding: "24px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontFamily: fonts.heading, fontSize: 16, fontWeight: 700, color: colors.text, margin: 0 }}>
                Resend to Unopened
              </h3>
              <button
                onClick={() => setResendTarget(null)}
                disabled={resendLoading}
                style={{ background: "none", border: "none", cursor: "pointer", color: colors.textSecondary, padding: 4 }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 16 }}>
              {resendTarget.title || resendTarget.subject}
            </div>

            {!resendResult ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: colors.textSecondary, display: "block", marginBottom: 4 }}>
                    Subject prefix
                  </label>
                  <input
                    type="text"
                    value={resendPrefix}
                    onChange={(e) => setResendPrefix(e.target.value)}
                    placeholder="e.g. ICYMI: "
                    style={{
                      width: "100%", padding: "8px 10px", fontSize: 13,
                      border: `1px solid ${colors.border}`, borderRadius: 6,
                      background: colors.bg, color: colors.text,
                      boxSizing: "border-box",
                    }}
                  />
                  <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>
                    Prepended to the original subject line
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, color: colors.textSecondary, display: "block", marginBottom: 6 }}>
                    Send method
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[
                      { id: "sendgrid", label: "SendGrid" },
                      { id: "o365", label: "O365 Send" },
                      { id: "draft", label: "O365 Draft" },
                    ].map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setResendMethod(m.id)}
                        style={{
                          flex: 1, padding: "6px 8px", fontSize: 12,
                          border: `1px solid ${resendMethod === m.id ? colors.accent : colors.border}`,
                          borderRadius: 6, cursor: "pointer",
                          background: resendMethod === m.id ? `${colors.accent}18` : colors.bg,
                          color: resendMethod === m.id ? colors.accent : colors.textSecondary,
                          fontWeight: resendMethod === m.id ? 600 : 400,
                        }}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  variant="primary"
                  icon={resendLoading ? Loader2 : Send}
                  onClick={handleResend}
                  disabled={resendLoading}
                  style={{ width: "100%" }}
                >
                  {resendLoading ? "Sending..." : "Resend to Unopened"}
                </Button>
              </>
            ) : resendResult.error ? (
              <div>
                <div style={{ color: colors.danger, fontSize: 13, marginBottom: 16, padding: "10px 12px", background: `${colors.danger}10`, borderRadius: 6 }}>
                  {resendResult.error}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button variant="secondary" onClick={() => setResendResult(null)} style={{ flex: 1 }}>
                    Try Again
                  </Button>
                  <Button variant="ghost" onClick={() => setResendTarget(null)} style={{ flex: 1 }}>
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ padding: "12px", background: `${colors.success}10`, borderRadius: 6, marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: colors.success, marginBottom: 4 }}>
                    Resend complete
                  </div>
                  <div style={{ fontSize: 13, color: colors.textSecondary }}>
                    {resendResult.sent} sent · {resendResult.failed || 0} failed · {resendResult.total} total unopened
                  </div>
                  {resendResult.message && (
                    <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>
                      {resendResult.message}
                    </div>
                  )}
                </div>
                <Button variant="secondary" onClick={() => setResendTarget(null)} style={{ width: "100%" }}>
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
